import axios from 'axios';
import { get } from 'lodash';
import { getAuthToken, setAuthToken, clearAuthToken } from '../utils/auth-token';

const REFRESH_TOKEN_INTERVAL = 1000 * 60 * 60 * 23; // 23 hours
const CLIENT_URL_ROOT = `${window.location.protocol}//${window.location.host}`;

const filterize = query => {
  const json = JSON.stringify(query);
  const encoded = encodeURI(json);
  return encoded;
}
class Api {
  constructor() {
    this.refreshTokenTimer = null;

    this.axios = axios.create({
      baseURL: '/api/v1/',
      timeout: 1000 * 60 * 5,
    });

    // Add a request interceptor
    this.axios.interceptors.request.use(config => {
      if (config.baseURL.indexOf('/api/v1') === 0 && !config.headers['authorization']) {
        const token = getAuthToken();
        if (token) config.headers['authorization'] = token;
      }
      return config;
    }, error => Promise.reject(error));

    this.axios.interceptors.response.use((response) => response, error => {
      if (['jwt expired', 'force login required'].includes(error.response?.data?.error?.message)) {
        clearAuthToken();
        this.stopRefreshTimer();
        window.location = '/login';
      }
      return Promise.reject(error);
    });

    this.refreshToken = this.refreshToken.bind(this);
    this.startRefreshTimer = this.startRefreshTimer.bind(this);
    this.stopRefreshTimer = this.stopRefreshTimer.bind(this);
  }

  error(err) {
    return get(err, 'response.data.error.message', get(err, 'message', 'Unknown'));
  }

  startRefreshTimer() {
    this.stopRefreshTimer();
    this.refreshTokenTimer = setTimeout(this.refreshToken, REFRESH_TOKEN_INTERVAL);
  }

  stopRefreshTimer() {
    clearTimeout(this.refreshTokenTimer);
  }

  async refreshToken() {
    try {
      const { data: newToken } = await this.axios.get('/refresh-token');
      setAuthToken(newToken);
      this.startRefreshTimer();
    } catch (err) {
      console.error(err);
    }
  }

  // async getItems() {
  //   await sleep(300); // API async call;
  //   const items = [];
  //   for (let i = 1; i <= 100; i++) {
  //     const title = faker.lorem.words((Math.random() * 10 + 1).toFixed(0));
  //     const thumb = faker.image.food(null, null, true);
  //     const desc = faker.lorem.paragraph();
  //     const price = (Math.random() * 17 + 5).toFixed(2);
  //     const item = {
  //       id: i,
  //       title,
  //       desc,
  //       thumb,
  //       price,
  //     }
  //     items.push(item);
  //   }
  //   return items;
  // };

  /**
   * User and login
   */
  async getCurrentUser() {
    try {
      if (getAuthToken()) {
        const { data: user } = await this.axios.get('/users/me');
        this.startRefreshTimer();
        return [null, user];
      } else {
        return [null, null];
      }
    } catch (error) {
      console.error(error);
      return [error, null];
    }
  };

  async login({ email, password }) {
    try {
      const { headers, data: user } = await this.axios.post('/login', { email, password });
      setAuthToken(headers['x-auth-token'])
      this.startRefreshTimer();
      return [null, user];
    } catch (err) {
      console.log('Error logging in', err);
      clearAuthToken();
      this.stopRefreshTimer();
      return [err, null];
    };
  };

  async proxyAs(userId) {
    try {
      const { headers, data: user } = await this.axios.post('/login/proxy', { userId });
      setAuthToken(headers['x-auth-token'])
      this.startRefreshTimer();
      return [null, user];
    } catch (error) {
      console.log('Error setting proxy for user', error);
      return [error, null];
    }
  }

  async logout() {
    this.stopRefreshTimer();
    clearAuthToken();
  }

  async changePassword(currentPassword, newPassword) {
    try {
      await this.axios.put('/users/me/password', {
        currentPassword,
        newPassword
      });
      return null;
    }
    catch (err) {
      console.error(err);
      return err;
    }
  }

  async updateProfile(userData) {
    try {
      const res = await this.axios.put('/users/me/profile', userData);
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async updateSettings(settings) {
    try {
      const res = await this.axios.put('/users/me/settings', settings);
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null]
    }
  }

  async updateAllowProxy(allowProxy) {
    try {
      const res = await this.axios.put('/users/me/allow-proxy', { allowProxy });
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null]
    }
  }

  async updateUser(id, updates) {
    try {
      const res = await this.axios.put(`/users/${id}`, updates);
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null]
    }
  }

  async getUsers() {
    try {
      const res = await this.axios.get('/users');
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null]
    }
  }

  async deleteMe() {
    try {
      const res = await this.axios.delete(`/users/me`);
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null]
    }
  }

  async getUserByEmail(search = '', filter = {}) {
    try {
      const trimmedSearch = search.trim();
      if (trimmedSearch) {
        const res = await this.axios.get(`/users/by-email/public?email=${trimmedSearch}&filter=filter=${filterize(filter)}`);
        return [null, res.data];
      } else {
        return [null, []];
      }
    } catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async inviteUser({ firstname, lastname, email, role }) {
    try {
      const clientUrl = `${CLIENT_URL_ROOT}/invitations`
      const res = await this.axios.post('/users/invitation', {
        firstname,
        lastname,
        email,
        role,
        clientUrl,
      });
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null]
    }
  }
  async shareWithUser({ firstname, lastname, email, groupId, listId }) {
    try {
      const clientUrl = `${CLIENT_URL_ROOT}/share/invitations`;
      const res = await this.axios.post('/invitations', {
        firstname,
        lastname,
        email,
        groupId,
        listId,
        clientUrl,
      });
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null]
    }
  }

  async getMyInvitations() {
    try {
      const { data } = await this.axios.get(`/invitations/mine`);
      return [null, data];
    } catch (error) {
      console.error(error);
      return [error, null]
    }
  }

  async getShareInvitation(invitationId) {
    try {
      const { data } = await this.axios.get(`/invitations/${invitationId}`);
      return [null, data];
    } catch (error) {
      console.error(error);
      return [error, null]
    }
  }

  async redeemSharedInvitation(invitationId, userData) {
    try {
      const { data } = await this.axios.post(`/users/invitations/${invitationId}/register`, userData);
      return [null, data];
    } catch (error) {
      console.error(error);
      return [error, null]
    }
  }

  async requestPwReset(email) {
    const clientUrl = `${CLIENT_URL_ROOT}/pw-reset`
    try {
      const res = await this.axios.post('/pwreset', { email, clientUrl })
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null]
    }
  }

  async getPwReset(token) {
    try {
      const res = await this.axios.get(`/pwreset/${token}`);
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async resetPassword(userId, token, password) {
    try {
      await this.axios.put(`/users/${userId}/password`, {
        token,
        password
      });
      return null;
    }
    catch (err) {
      console.error(err);
      return err;
    }
  }

  async getInvitation(invitation) {
    try {
      const res = await this.axios.get(`/users/invitation/${invitation}`);
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null]
    }
  }

  async getConfig() {
    try {
      const res = await this.axios.get('/config');
      return [null, res.data];
    } catch (err) {
      console.error(err);
      return [err, null]
    }
  }

  /**
   * Apps and Devices
   */

  async getAppDevices({ appId, limit, skip }) {
    try {
      const { data } = await this.axios(`/apps/${appId}/devices?limit=${limit}&skip=${skip}`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async getAppDeviceEvents({ appId, deviceId, filter = {}, limit = 20, skip = 0 }) {

    try {
      const { data } = await this.axios(`/apps/${appId}/devices/${deviceId}/events?limit=${limit}&skip=${skip}&filter=${filterize(filter)}`);
      return [null, data];
    } catch (err) {
      console.log(err);
      return [err, null]
    }
  }

  async getMyApps() {
    try {
      const { data } = await this.axios.get('/apps/mine');
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async getAppDetails(appId) {
    try {
      const { data } = await this.axios.get(`/apps/${appId}`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async updateApp(appId, updates = {}) {
    try {
      const { data } = await this.axios.put(`/apps/${appId}`, updates);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async createList({ name, source = '', url = '', eventDate = null, image = '' }) {
    try {
      const { data } = await this.axios.post(`/lists/`, {
        name, url, source, eventDate, image
      });
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async updateList(listId, { name, description, image, eventDate, }) {
    try {
      const { data } = await this.axios.put(`/lists/${listId}`, {
        name, eventDate, image, description,
      });
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async importList(listId) {
    try {
      const { data } = await this.axios.get(`/lists/${listId}/import`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async addItemToList(listId, item) {
    try {
      const { data } = await this.axios.post(`lists/${listId}/items`, item);
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async updateItem(listId, itemId, updates) {
    try {
      const { data } = await this.axios.put(`lists/${listId}/items/${itemId}`, updates);
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  } em

  async claimItem(item) {
    try {
      const { data } = await this.axios.put(`items/${item._id}/claim`, { claim: true });
      return [null, data];
    } catch (error) {
      console.error(error);
      return [error, null]
    }
  }

  async releaseItem(item) {
    try {
      const { data } = await this.axios.put(`items/${item._id}/claim`, { claim: false });
      return [null, data];
    } catch (error) {
      console.error(error);
      return [error, null]
    }
  }

  async addSourceToList(listId, url) {
    try {
      const { data } = await this.axios.post(`lists/${listId}/sources`, { url });
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }
  async getList(id, context = {}) {
    try {
      const { model, id: contextId } = context;
      let query = '';
      if (model && contextId) {
        query = `?contextModel=${model}&contextId=${contextId}`;
      }
      const { data } = await this.axios.get(`/lists/${id}${query}`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async getMyLists() {
    try {
      const { data } = await this.axios.get(`/lists/mine`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async getFriendsLists() {
    try {
      const { data } = await this.axios.get(`/lists/shared-with-me`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async getMySharedLists() {
    try {
      const { data } = await this.axios.get(`/lists/shared-by-me`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async getMyGroupGifts(groupId) {
    try {
      const { data } = await this.axios.get(`/items/reserved-by-me/group/${groupId}`);
      return [null, data];
    } catch (error) {
      console.log(error);
      return [error, null];
    }
  }

  async shareList({ groupId, listId }) {
    try {
      const clientUrl = `${CLIENT_URL_ROOT}/lists`;
      const { data } = await this.axios.post(`/lists/${listId}/shares`, { groupId, clientUrl });
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async unshareList({ userId, groupId, listId }) {
    try {
      let id = null;
      let model = null;
      if (groupId) {
        model = 'groups';
        id = groupId;
      } else if (userId) {
        model = 'users';
        id = userId;
      }
      const { data } = await this.axios.delete(`/lists/${listId}/shares/${model}/${id}`);
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async setListCollaboration({ groupId, userId, listId, allow }) {
    try {
      let id = null;
      let model = null;
      if (groupId) {
        model = 'groups';
        id = groupId;
      } else if (userId) {
        model = 'users';
        id = userId;
      }
      const { data } = await this.axios.put(`/lists/${listId}/collaboration/${model}/${id}`, { allow });
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async deleteList(id) {
    try {
      const { data } = await this.axios.delete(`lists/${id}`);
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async deleteListItem({ listId, itemId }) {
    try {
      const { data } = await this.axios.delete(`lists/${listId}/items/${itemId}`);
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async postComment({ parent, parentModel, contextId, context, text, mentions }) {
    try {
      const { data } = await this.axios.post('comments', {
        parent,
        parentModel,
        contextId,
        context,
        text,
        mentions,
      });
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async getComments(filter = {}) {
    try {
      const { data } = await this.axios.get(`comments?filter=${filterize(filter)}`);
      return [null, data];
    } catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async toggleItemLike(listId, itemId, { model: context, id: contextId, emoji } = {}) {
    try {
      const { data } = await this.axios.put(`lists/${listId}/items/${itemId}/likes`, {
        contextId,
        context,
        emoji
      });
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async uploadFile(file) {
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await this.axios.post('/file-uploads',
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return [null, data];
    } catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async getMyGroups() {
    try {
      const { data } = await this.axios.get(`/groups/mine`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async getFriendsGroups() {
    try {
      const { data } = await this.axios.get(`/groups/shared-with-me`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async createGroup({ name, url }) {
    try {
      const { data } = await this.axios.post(`/groups/`, {
        name, url,
      });
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async getGroup(id) {
    try {
      const { data } = await this.axios.get(`/groups/${id}`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async addAdminToGroup({ groupId, userId }) {
    try {
      const { data } = await this.axios.post(`/groups/${groupId}/admins/`, { userId });
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async removeMemberFromGroup({ groupId, userId }) {
    try {
      const { data } = await this.axios.delete(`/groups/${groupId}/members/${userId}`);
      return [null, data];
    } catch (err) {
      console.error(err);
      return [err, null];
    }
  }

  async addEventToGroup(groupId, eventData) {
    try {
      const { data } = await this.axios.post(`/groups/${groupId}/events`, eventData);
      return [null, data];
    } catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async deleteGroup(id) {
    try {
      const { data } = await this.axios.delete(`groups/${id}`);
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async getMyNotifications() {
    try {
      const { data } = await this.axios.get(`notifications/mine`);
      return [null, data];
    }
    catch (error) {
      console.error(error);
      return [error, null];
    }
  }

  async deleteNotification(id) {
    try {
      const { data } = await this.axios.delete(`notifications/${id}`);
      return [null, data];
    } catch (error) {
      console.error(error);
      return [error, null];
    }
  }

};

const api = new Api();
export default api